// The Excel report (Documentation/excel-report): "All data" (sheet 1 of the customer's Week 43 file), the chosen
// layout's sheet (layoutSheet.js), and the Colors sheet (the legend and notes on the data). The 7 work sheets of the
// Week 43 file are left out of the export (the customer's choice, 30-10-2026); their code stays, and
// WORK_SHEETS_IN_EXPORT brings them back.
import { addBaseSheet } from './baseSheet';
import { addColorsSheet } from './colorsSheet';
import { addLayoutSheet } from './layoutSheet';
import { addWorkSheets } from './workSheetStyle';

/** Whether the export has the 7 work sheets (Set-Up ... Harvest) after "All data". */
export const WORK_SHEETS_IN_EXPORT = false;

// A CO as a number to sort by: a number first, then other text, empty last
const coKey = (co) => {
  const text = String(co ?? '').trim();
  if (text === '') return [2, 0, ''];
  return Number.isFinite(Number(text)) ? [0, Number(text), ''] : [1, 0, text];
};
const natural = (a, b) => String(a ?? '').localeCompare(String(b ?? ''), 'en', { numeric: true, sensitivity: 'base' });

/** The trial rows in the report's order, the same on every sheet: by CO (as a number, empty last), then Trial (natural order). */
export function sortTrials(trials) {
  return [...trials].sort((a, b) => {
    const [x, y] = [coKey(a.co), coKey(b.co)];
    return x[0] - y[0] || x[1] - y[1] || natural(x[2], y[2]) || natural(a.trialno, b.trialno) || a.id - b.id;
  });
}

/**
 * Fills an ExcelJS workbook with the report for the trial rows of POST /v1/trials/export-rows (sorted by sortTrials),
 * as on `exportDate` (a task is overdue when it ended before that day). "All data" is the sheet the file opens on:
 * the customer's Work_Sorting.py reads the active sheet. `layout` ({ name, fields, trials: as POST /v1/trials/{ids}
 * returns them }) adds the layout's sheet after it; `workSheets` the 7 work sheets.
 * Returns what the report says about the data: { trials, gpsSwapped (trials whose GPS fields were the other way
 * round), duplicates (tasks sharing a number on a trial), notShown (visits / applications without a column),
 * sheets: { 'Set-Up': rows, ... } (empty without the work sheets), layoutSheet: { name, rows, columns } or null }.
 */
export function buildExcelReport(workbook, unsorted, exportDate = new Date(), { workSheets = WORK_SHEETS_IN_EXPORT, layout = null } = {}) {
  const trials = sortTrials(unsorted);
  const rows = addBaseSheet(workbook, trials, exportDate);
  const layoutSheet = layout ? addLayoutSheet(workbook, { ...layout, exportDate }) : null;
  const sheets = workSheets ? addWorkSheets(workbook, rows, exportDate) : {};
  const summary = {
    trials: rows.length,
    gpsSwapped: rows.filter((r) => r.gpsSwapped).length,
    duplicates: rows.reduce((n, r) => n + r.duplicates, 0),
    notShown: rows.reduce((n, r) => n + r.notShown, 0),
    sheets: Object.fromEntries(Object.entries(sheets).map(([name, list]) => [name, list.length])),
    layoutSheet,
    exportDate,
  };
  addColorsSheet(workbook, summary, { workSheets, layoutSheetName: layoutSheet?.name ?? null });
  // Open on "All data" (the first sheet)
  workbook.views = [{ x: 0, y: 0, width: 28800, height: 15000, firstSheet: 0, activeTab: 0, visibility: 'visible' }];
  return summary;
}
